import React, { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid2 from "@mui/material/Grid2";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";

import { createUser } from "_DatabaseFunctions/cloudCalls/auth";

import { auth } from "config/firebase";
import {
  sendEmailVerification as firebaseSendEmailVerification,
  onAuthStateChanged,
} from "firebase/auth";
import { validateInput } from "./utils/ValidateInput";

interface SignUpProps {
  setSignin: () => void;
}

export default function SignUp(props: SignUpProps) {
  const [values, setValues] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
    submitMsg: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      setValues({ ...values, submitMsg: "You must accept the Terms of Service to create an account" });
      return;
    }
    
    const errs = validateInput(values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setValues({ ...values, submitMsg: "Check Input Fields for Errors" });
    } else {
      setIsLoading(true);
      setValues({ ...values, submitMsg: "" });
      signUserUp();
    }
  };

  const signUserUp = async () => {
    try {
      //! todo:Need to get cloud function method of createUser to work with emulator
      // await auth.createUserWithEmailAndPassword(values.email, values.password);
      await createUser({
        ...values,
        city: "",
        state: "",
      });

      // Wait for auth state to update, then send verification email
      console.log("User created, waiting for auth state update...");
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user && user.email === values.email) {
          console.log(
            "Auth state updated, sending verification email to:",
            user.email,
          );
          try {
            await firebaseSendEmailVerification(user);
            console.log("Verification email sent successfully");
          } catch (emailError) {
            console.error("Error sending verification email:", emailError);
          }
          unsubscribe(); // Clean up the listener
        }
      });
      setValues({
        ...values,
        submitMsg: `Email verifcation link sent to ${values.email}\n Click the link then sign in to continue`,
      });
    } catch (err: any) {
      setValues({
        ...values,
        submitMsg: err.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Avatar 
        sx={{ 
          mt: 3, 
          mb: 2,
          backgroundColor: 'primary.main' 
        }} 
      />
      <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
        Sign Up
      </Typography>
      <Box component="form" noValidate sx={{ width: '100%' }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TextField
              value={values.displayName}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              id="displayName"
              label="Display Name"
              name="displayName"
              autoComplete="displayName"
              helperText={errors.displayName}
              error={!!errors.displayName}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              value={values.email}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              helperText={errors.email}
              error={!!errors.email}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              value={values.password}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              helperText={errors.password}
              error={!!errors.password}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              value={values.confirmPassword}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="password"
              autoComplete="current-password"
              helperText={errors.confirmPassword}
              error={!!errors.confirmPassword}
            />
          </Grid2>
          <Grid2 size={12}>
            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    name="termsAccepted"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read and agree to the{' '}
                    <Link href="/terms" target="_blank" rel="noopener">
                      Terms of Service
                    </Link>
                    , including compliance with all local laws regarding poker games
                  </Typography>
                }
              />
            </Box>
          </Grid2>
        </Grid2>
        {isLoading ? (
          <>
            <div style={{ color: "red" }}>Creating Account...</div>
            <CircularProgress />
          </>
        ) : (
          <Button
            type="submit"
            onClick={handleSubmit}
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
          >
            Sign Up
          </Button>
        )}
        {values.submitMsg && (
          <Typography 
            variant="body2" 
            color="error" 
            sx={{ mt: 2, textAlign: 'center' }}
          >
            {values.submitMsg}
          </Typography>
        )}
        <Grid2 container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Grid2>
            <Link href="#" onClick={props.setSignin} variant="body2">
              Already have an account? Sign in
            </Link>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
}
